import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon.js';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ToastTone = 'success' | 'error' | 'info' | 'warning';

export interface ToastInput {
  message: string;
  description?: string;
  tone?: ToastTone;
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastState extends Required<Omit<ToastInput, 'description' | 'action'>> {
  id: string;
  description?: string;
  action?: ToastInput['action'];
  removing: boolean;
}

interface ToastContextValue {
  show: (input: ToastInput) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

// ── Context ───────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

// ── Tone colours ──────────────────────────────────────────────────────────────

const TONE_CLR: Record<ToastTone, string> = {
  success: '#22C55E',
  error:   'var(--evo-danger)',
  warning: 'var(--evo-amber)',
  info:    'var(--evo-cyan)',
};

// ── ToastItem ─────────────────────────────────────────────────────────────────

const MAX_VISIBLE = 5;
const DEFAULT_DURATION = 4000;

// ── Dos avisos idénticos a la vez son uno solo ───────────────────────────────
// Nunca son información nueva, y el segundo tapa lo que el usuario estaba
// mirando. La fuente típica es estructural, no un descuido de un formulario: el
// manejador global de errores de mutación (main.tsx) avisa por cualquier
// mutación sin `onError`, y un caller que envuelve su `mutateAsync` en
// try/catch y toastea ahí también tiene las dos vías abiertas. Ese patrón está
// en una docena larga de pantallas; taparlo caller por caller no evita la
// próxima.
//
// La ventana es corta a propósito: dos emisiones del mismo evento caen en el
// mismo tick, así que un reintento de verdad (un click más, con una ida al
// servidor de por medio) queda afuera y se sigue avisando.
const DEDUPE_MS = 1000;

/** Identidad de un aviso a efectos de repetición. `null` = no se deduplica. */
export function claveDeToast(input: ToastInput): string | null {
  // Un toast con acción no se deduplica: el botón puede hacer otra cosa aunque
  // el texto coincida.
  if (input.action) return null;
  return `${input.tone ?? 'info'}|${input.message}|${input.description ?? ''}`;
}

/** ¿Este aviso es la repetición inmediata del anterior? */
export function esRepeticion(
  ultimo: { clave: string; at: number } | null,
  clave: string | null,
  ahora: number,
): boolean {
  if (clave == null || ultimo == null) return false;
  return ultimo.clave === clave && ahora - ultimo.at < DEDUPE_MS;
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastState;
  onDismiss: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [progressDuration, setProgressDuration] = useState(toast.duration);
  const [progressKey, setProgressKey] = useState(0);
  const remainingRef = useRef(toast.duration);
  const startRef     = useRef(Date.now());
  const timerRef     = useRef<ReturnType<typeof setTimeout>>();

  const clr = TONE_CLR[toast.tone];

  useEffect(() => {
    if (toast.duration === 0) return;
    timerRef.current = setTimeout(() => onDismiss(toast.id), remainingRef.current);
    return () => clearTimeout(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMouseEnter = () => {
    if (toast.duration === 0) return;
    clearTimeout(timerRef.current);
    remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startRef.current));
  };

  const onMouseLeave = () => {
    if (toast.duration === 0 || remainingRef.current <= 0) return;
    startRef.current = Date.now();
    setProgressDuration(remainingRef.current);
    setProgressKey(k => k + 1);
    timerRef.current = setTimeout(() => onDismiss(toast.id), remainingRef.current);
  };

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'relative',
        width: '320px',
        background: 'var(--evo-bg-3)',
        border: '1px solid var(--evo-stroke)',
        borderLeft: `3px solid ${clr}`,
        borderRadius: '6px',
        padding: '12px 14px 14px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
        overflow: 'hidden',
        animation: toast.removing
          ? 'toast-out 220ms ease forwards'
          : 'toast-in 200ms ease both',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      {/* Top row: message + dismiss */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <p
          style={{
            flex: 1,
            margin: 0,
            fontSize: '13px',
            fontWeight: 500,
            color: 'var(--evo-fg-1)',
            lineHeight: 1.4,
          }}
        >
          {toast.message}
        </p>
        <button
          onClick={() => onDismiss(toast.id)}
          aria-label={t('common.close')}
          title={t('common.close')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '18px',
            height: '18px',
            flexShrink: 0,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--evo-fg-3)',
            padding: 0,
            marginTop: '1px',
          }}
        >
          <Icon name="close" size={11} />
        </button>
      </div>

      {/* Description */}
      {toast.description && (
        <p
          style={{
            margin: 0,
            fontSize: '12px',
            color: 'var(--evo-fg-3)',
            lineHeight: 1.5,
          }}
        >
          {toast.description}
        </p>
      )}

      {/* Action */}
      {toast.action && (
        <button
          onClick={() => { toast.action!.onClick(); onDismiss(toast.id); }}
          style={{
            alignSelf: 'flex-start',
            marginTop: '4px',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 600,
            color: clr,
          }}
        >
          {toast.action.label}
        </button>
      )}

      {/* Progress bar */}
      {toast.duration > 0 && (
        <div
          key={progressKey}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: '2px',
            width: '100%',
            background: clr,
            transformOrigin: 'left',
            animation: `toast-progress ${progressDuration}ms linear forwards`,
          }}
        />
      )}
    </div>
  );
}

// ── Puente para emitir desde fuera de React ──────────────────────────────────
// El manejador global de errores de mutación (main.tsx) vive fuera del árbol de
// componentes, así que no puede usar useToast(). El provider registra acá su
// `show` al montarse.
let _show: ((input: ToastInput) => void) | null = null;

/** Emite un toast desde fuera de React. Si el provider todavía no montó, no
 *  hace nada — mejor perder un toast que romper el arranque. */
export function notifyToast(input: ToastInput): void {
  _show?.(input);
}

// ── ToastProvider ─────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const counterRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts(prev =>
      prev.map(t => t.id === id ? { ...t, removing: true } : t),
    );
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 230);
  }, []);

  const ultimoRef = useRef<{ clave: string; id: string; at: number } | null>(null);

  const show = useCallback((input: ToastInput): string => {
    const clave = claveDeToast(input);
    const ahora = Date.now();
    const ultimo = ultimoRef.current;
    if (ultimo && esRepeticion(ultimo, clave, ahora)) return ultimo.id;

    const id = `toast-${++counterRef.current}`;
    if (clave != null) ultimoRef.current = { clave, id, at: ahora };
    const toast: ToastState = {
      id,
      message:  input.message,
      tone:     input.tone     ?? 'info',
      duration: input.duration ?? DEFAULT_DURATION,
      removing: false,
      ...(input.description != null ? { description: input.description } : {}),
      ...(input.action      != null ? { action:      input.action      } : {}),
    };
    setToasts(prev => {
      const next = [...prev, toast];
      return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
    });
    return id;
  }, []);

  const dismissAll = useCallback(() => {
    setToasts(prev => prev.map(t => ({ ...t, removing: true })));
    setTimeout(() => setToasts([]), 230);
  }, []);

  useEffect(() => {
    _show = show;
    return () => { if (_show === show) _show = null; };
  }, [show]);

  return (
    <ToastContext.Provider value={{ show, dismiss, dismissAll }}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          aria-label={t('common.notifications')}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 900,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            pointerEvents: 'none',
          }}
        >
          {toasts.map(t => (
            <div key={t.id} style={{ pointerEvents: 'auto' }}>
              <ToastItem toast={t} onDismiss={dismiss} />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

// ── useToast ──────────────────────────────────────────────────────────────────

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');

  return {
    show:       ctx.show,
    dismiss:    ctx.dismiss,
    dismissAll: ctx.dismissAll,
    success: (message: string, opts?: Omit<ToastInput, 'message' | 'tone'>) =>
      ctx.show({ ...opts, message, tone: 'success' }),
    error:   (message: string, opts?: Omit<ToastInput, 'message' | 'tone'>) =>
      ctx.show({ ...opts, message, tone: 'error' }),
    info:    (message: string, opts?: Omit<ToastInput, 'message' | 'tone'>) =>
      ctx.show({ ...opts, message, tone: 'info' }),
    warning: (message: string, opts?: Omit<ToastInput, 'message' | 'tone'>) =>
      ctx.show({ ...opts, message, tone: 'warning' }),
  };
}
